import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, CalendarCheck, Send, X } from "lucide-react";
import { EASE } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_PROMPTS = [
  "Welche Leistungen bietet ihr?",
  "Wie läuft ein Projekt ab?",
  "Was kostet eine Fassade?",
  "Wo genau seid ihr?",
];

const GREETING = {
  role: "assistant",
  content:
    "Moin! Ich bin der digitale Assistent von Manfred Berglar, Malermeister. Fragen Sie mich gern zu Leistungen, Ablauf oder Terminen – oder rufen Sie direkt an: 0 27 61 / 6 50 64.",
};

const initialTermin = { typ: "Rueckruf", name: "", telefon: "", zeitraum: "", nachricht: "" };

const TypingDots = () => (
  <span className="inline-flex gap-1 items-center py-1" data-testid="chat-typing">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-gold"
        animate={{ opacity: [0.25, 1, 0.25] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.18 }}
      />
    ))}
  </span>
);

const fieldCls =
  "w-full bg-surface border border-line focus:border-gold outline-none px-3 py-2.5 text-sm text-ink placeholder:text-muted/60 transition-colors duration-300 rounded-lg";

export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [terminOpen, setTerminOpen] = useState(false);
  const [termin, setTermin] = useState(initialTermin);
  const [terminBusy, setTerminBusy] = useState(false);
  const sessionRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem("berglar-chat-session");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("berglar-chat-session", sid);
    }
    sessionRef.current = sid;
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, terminOpen]);

  const appendToken = (token) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
      return copy;
    });

  const pushAssistant = (content) =>
    setMessages((m) => [...m, { role: "assistant", content }]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setTerminOpen(false);
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const payload = part.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.token) appendToken(data.token);
            if (data.error) appendToken(data.error);
          } catch {
            /* ignore partial chunks */
          }
        }
      }
    } catch {
      pushAssistant("Entschuldigung, ich bin gerade nicht erreichbar. Rufen Sie uns gern an: 0 27 61 / 6 50 64.");
      setMessages((m) => m.filter((x, i) => !(i === m.length - 2 && x.content === "")));
    }
    setBusy(false);
  };

  const submitTermin = async (e) => {
    e.preventDefault();
    if (terminBusy) return;
    setTerminBusy(true);
    try {
      const res = await fetch(`${API}/api/termin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(termin),
      });
      if (!res.ok) throw new Error();
      const typLabel = termin.typ === "Rueckruf" ? "Rückruf" : "Vor-Ort-Termin";
      pushAssistant(
        `Alles notiert, ${termin.name}! Ihre Anfrage (${typLabel}, Zeitraum: ${termin.zeitraum}) ist bei uns eingegangen. Wir melden uns zeitnah unter ${termin.telefon}.`
      );
      setTermin(initialTermin);
      setTerminOpen(false);
    } catch {
      pushAssistant(
        "Das hat leider nicht geklappt. Bitte versuchen Sie es erneut – oder rufen Sie direkt an: 0 27 61 / 6 50 64."
      );
    }
    setTerminBusy(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Chat schließen" : "KI-Assistent öffnen"}
        className="fixed bottom-5 right-5 z-50 h-14 pl-4 pr-5 max-[380px]:h-12 max-[380px]:pl-3 max-[380px]:pr-4 rounded-full bg-gold text-white flex items-center gap-2.5 shadow-[0_12px_32px_-8px_rgba(110,86,54,0.5)] transition-[background-color,transform] duration-300 hover:bg-golddark hover:scale-[1.03]"
      >
        {open ? <X size={20} /> : <Bot size={20} />}
        <span className="text-sm font-semibold tracking-wide">{open ? "Schließen" : "Fragen?"}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm h-[72vh] max-h-[560px] bg-white border border-line rounded-2xl shadow-[0_20px_48px_-16px_rgba(21,21,21,0.28)] flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 bg-white border-b border-line">
              <img src="/images/wappen.png" alt="" className="h-10 w-auto shrink-0" />
              <div className="min-w-0">
                <p className="font-display text-lg font-semibold text-ink leading-none">Berglar Assistent</p>
                <p className="text-[11px] text-muted tracking-[0.18em] uppercase font-semibold mt-1">
                  Antwortet in Sekunden
                </p>
              </div>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" data-testid="chat-message-list">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed rounded-xl ${
                    m.role === "user" ? "self-end bg-gold text-white rounded-br-sm" : "self-start bg-surface text-ink/85 rounded-bl-sm"
                  }`}
                >
                  {m.content === "" && m.role === "assistant" ? <TypingDots /> : m.content}
                </div>
              ))}
              {messages.length === 1 && !busy && !terminOpen && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {QUICK_PROMPTS.map((q, i) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`chat-quick-${i}`}
                      className="text-xs border border-line rounded-full text-muted px-3 py-2 hover:border-gold hover:text-gold transition-colors duration-300"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <AnimatePresence>
                {terminOpen && (
                  <motion.form
                    key="termin"
                    onSubmit={submitTermin}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    data-testid="chat-termin-form"
                    className="border border-gold/30 bg-white rounded-xl p-4 flex flex-col gap-3"
                  >
                    <div className="flex gap-2">
                      {[
                        { v: "Rueckruf", label: "Rückruf" },
                        { v: "Vor-Ort-Termin", label: "Vor-Ort-Termin" },
                      ].map((o) => (
                        <button
                          type="button"
                          key={o.v}
                          onClick={() => setTermin((t) => ({ ...t, typ: o.v }))}
                          data-testid={`termin-typ-${o.v === "Rueckruf" ? "rueckruf" : "vorort"}`}
                          className={`flex-1 text-xs font-semibold uppercase tracking-wide py-2.5 border rounded-full transition-colors duration-300 ${
                            termin.typ === o.v
                              ? "bg-gold text-white border-gold"
                              : "border-line text-muted hover:border-gold/60"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    <input
                      required
                      value={termin.name}
                      onChange={(e) => setTermin((t) => ({ ...t, name: e.target.value }))}
                      data-testid="termin-input-name"
                      placeholder="Ihr Name *"
                      className={fieldCls}
                    />
                    <input
                      required
                      type="tel"
                      value={termin.telefon}
                      onChange={(e) => setTermin((t) => ({ ...t, telefon: e.target.value }))}
                      data-testid="termin-input-telefon"
                      placeholder="Telefonnummer *"
                      className={fieldCls}
                    />
                    <input
                      required
                      value={termin.zeitraum}
                      onChange={(e) => setTermin((t) => ({ ...t, zeitraum: e.target.value }))}
                      data-testid="termin-input-zeitraum"
                      placeholder="Wunschzeitraum, z. B. nächste Woche vormittags *"
                      className={fieldCls}
                    />
                    <textarea
                      rows={2}
                      value={termin.nachricht}
                      onChange={(e) => setTermin((t) => ({ ...t, nachricht: e.target.value }))}
                      data-testid="termin-input-nachricht"
                      placeholder="Worum geht es? (optional)"
                      className={`${fieldCls} resize-none`}
                    />
                    <button
                      type="submit"
                      data-testid="chat-termin-submit"
                      disabled={terminBusy}
                      className="btn-primary !py-3 w-full disabled:opacity-50"
                    >
                      {terminBusy ? "Wird gesendet …" : "Anfrage senden"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
            <div className="border-t border-line bg-white">
              <button
                onClick={() => setTerminOpen((o) => !o)}
                data-testid="chat-termin-open"
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                  terminOpen ? "text-gold" : "text-muted hover:text-gold"
                }`}
              >
                <CalendarCheck size={15} /> Rückruf oder Termin anfragen
              </button>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-2 px-3 pb-3"
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  data-testid="chat-input"
                  placeholder="Ihre Frage an uns …"
                  maxLength={500}
                  className="flex-1 bg-transparent text-sm text-ink placeholder:text-muted/60 outline-none px-2 py-2"
                />
                <button
                  type="submit"
                  data-testid="chat-send-button"
                  disabled={busy || !input.trim()}
                  aria-label="Nachricht senden"
                  className="w-10 h-10 shrink-0 rounded-full bg-gold text-white flex items-center justify-center transition-colors duration-300 hover:bg-golddark disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </form>
              <p className="text-[10px] text-muted text-center pb-2">
                KI-Assistent · Angaben ohne Gewähr ·{" "}
                <a href="/datenschutz" data-testid="chat-privacy-link" className="underline underline-offset-2 hover:text-golddark transition-colors">
                  Datenschutz
                </a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
